import {
  Component,
  EventEmitter,
  Input,
  Output,
  type SimpleChanges,
  OnInit,
  OnChanges
} from '@angular/core';
import {
  FlightItineraryWithExtensions,
  ServiceProvider,
  UserFavorurite,
  convertFlightJourneyToInputFlightJourney
} from '@sabstravtech/obtservices/base';
import {
  FlightSeatMapSeats,
  FlightExtrasOptions,
  UserService,
  FlightSeatMap,
  EnterpriseSearchService,
  ServiceType,
  FlightOptions,
  ModalOpenerService,
  User,
  PreferenceKey,
  UserPreference,
  WithSubscriptionComponent
} from '@sabstravtech/obtservices/angular';
import _ from 'lodash';
import { NgbDateStruct, NgbNavChangeEvent } from '@ng-bootstrap/ng-bootstrap';
import { lastValueFrom } from 'rxjs';

import { LightningUserFavorurite } from '../../../../../core/classes/user-favourite.enum';
import { LightningModalTypes } from '../../../../../core/classes/modal-types.enum';
import { FlightSeat } from '../flight-seat-map/flight-set.interface';

@Component({
  selector: 'app-flight-seat-map-tab',
  templateUrl: './flight-seat-map-tab.component.html',
  styleUrls: ['./flight-seat-map-tab.component.scss'],
  standalone: false
})
export class FlightSeatMapTabComponent
  extends WithSubscriptionComponent
  implements OnInit, OnChanges
{
  ServiceProvider: typeof ServiceProvider = ServiceProvider;
  readonly permittedServicesLCC: string[] = [ServiceProvider.Travelfusion];
  readonly permittedServicesGDS: string[] = [
    ServiceProvider.SabreFlight,
    ServiceProvider.TravelportFlight,
    ServiceProvider.AmadeusFlight
  ];
  readonly permittedServicesExtras: string[] = [
    ServiceProvider.Travelfusion,
    ServiceProvider.TravelportFlight,
    ServiceProvider.AmadeusFlight
  ];
  readonly permittedServicesNDC: string[] = [ServiceProvider.AmadeusNDC];
  @Input() parentTabId = '';
  @Input() flight: FlightItineraryWithExtensions = null;
  @Input() supplier = '';
  @Input() travellerId = '';
  @Input() userName = '';
  @Input() travellerDob: number = null;
  @Input() travellerIndex = 0;
  @Input() isGuest = false;
  @Input() selectedSeatWithId: { [x: string]: FlightSeatMapSeats[] } = {};
  @Output() seat: EventEmitter<{ selectedSeat: FlightSeatMapSeats; id: string }> =
    new EventEmitter();
  @Output() onNotValid: EventEmitter<boolean> = new EventEmitter();
  @Output() flightExtras: EventEmitter<any> = new EventEmitter<any>();
  @Output() selectedFlightExtrasChange: EventEmitter<any> = new EventEmitter<any>();
  @Output() loyaltyCode: EventEmitter<any> = new EventEmitter<any>();
  @Output() processed: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() loyaltyCodesChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() activeFlightID: EventEmitter<{ flightId: string; travellerId: string }> =
    new EventEmitter<{ flightId: string; travellerId: string }>();
  private disableLoyalty = false;
  private isTFXLLC = false;
  private firstTab = 0;
  private userLoyaltyCode: { [key: string]: string } = {};
  private inboundFlightsSeats: FlightSeatMap[] = [];
  private outboundFlightsSeats: FlightSeatMap[] = [];
  public inboundFlightsExtras: FlightOptions[] = [];
  public outboundFlightsExtras: FlightOptions[] = [];
  public showSeatmap = false;
  public showAllExtras = false;
  public loyaltyCodes: string[] = [];
  public activeRequests: boolean[] = [];
  public showLoyalty = false;
  public luggagePerBooking = false;
  public luggagePerPax = false;
  public ancillaryPerBooking = false;
  public activeTab: string;
  public seatsReceived = false;
  public user: User = null;
  public seatingPreference: string = null;
  public outboundSeats: FlightSeatMap[];
  public inboundSeats: FlightSeatMap[];
  public extrasPlaceholder: FlightExtrasOptions;
  public baggagePlaceholder: FlightExtrasOptions;
  public ancillaryPlaceholder: FlightExtrasOptions;
  dobMandatory = false;
  travellerDobConvertedToString: string;
  constructor(
    public userService: UserService,
    public searchService: EnterpriseSearchService,
    private modalService: ModalOpenerService
  ) {
    super();
  }

  ngOnInit() {
    const seatMapConfigs = this.userService.getUserFavoriteObject<{
      GDS: { showSeatMaps: boolean };
      SabreLowCost: { showSeatMaps: boolean; showExtras: boolean };
      LCC: { showSeatMaps: boolean; showExtras: boolean };
      NDC: { showSeatMaps: boolean };
    }>(LightningUserFavorurite.FlightResultsConfiguration);

    const allowAgentToViewSeatMaps = this.userService.getUserFavoriteObject<{
      GDS: { showSeatMaps: boolean };
      SabreLowCost: { showSeatMaps: boolean };
      LCC: { showSeatMaps: boolean };
      NDC: { showSeatMaps: boolean };
    }>(LightningUserFavorurite.AllowAgentToViewSeatMaps);
    const userIsAgent = this.userService.userIsAgent();

    // sabre ryanair is part of LCC
    const isSabreRyanair =
      this.supplier === ServiceProvider.SabreFlight &&
      [...this.flight.outboundFlights, ...this.flight.inboundFlights].some(
        (flight) =>
          flight.marketingCarrierName === 'Ryanair' || flight.operatingCarrierName === 'Ryanair'
      );
    if (isSabreRyanair) {
      this.showSeatmap =
        seatMapConfigs?.SabreLowCost?.showSeatMaps ||
        (userIsAgent && allowAgentToViewSeatMaps?.SabreLowCost?.showSeatMaps);
    } else if (this.permittedServicesLCC.includes(this.supplier)) {
      this.showSeatmap =
        seatMapConfigs?.LCC?.showSeatMaps ||
        (userIsAgent && allowAgentToViewSeatMaps?.LCC?.showSeatMaps);
    } else if (this.permittedServicesGDS.includes(this.supplier)) {
      this.showSeatmap =
        seatMapConfigs?.GDS?.showSeatMaps ||
        (userIsAgent && allowAgentToViewSeatMaps?.GDS?.showSeatMaps);
    } else if (this.permittedServicesNDC.includes(this.supplier)) {
      this.showSeatmap =
        seatMapConfigs?.NDC?.showSeatMaps ||
        (userIsAgent && allowAgentToViewSeatMaps?.NDC?.showSeatMaps);
    }

    this.disableLoyalty = this.userService.getUserFavoriteObject<{ disableLoyalty: boolean }>(
      UserFavorurite.DisableLoyalty
    )?.disableLoyalty;

    this.showAllExtras = isSabreRyanair
      ? seatMapConfigs?.SabreLowCost?.showExtras
      : this.permittedServicesExtras.includes(this.flight.source);

    // * For now load all the seat map once for TFX
    // TODO this component needs to be refactored to be the same as Scion
    this.isTFXLLC = this.permittedServicesLCC.includes(this.supplier);

    if (this.showSeatmap) {
      this.showLoyalty =
        (this.permittedServicesGDS.includes(this.supplier) ||
          this.permittedServicesLCC.includes(this.supplier)) &&
        !this.disableLoyalty;

      const flights = [...this.flight.outboundFlights, this.flight.inboundFlights];
      flights.forEach((f) => {
        this.loyaltyCodes.push('');
        this.activeRequests.push(false);
      });

      this.loadFlightsSeats(this.isTFXLLC)
        .then(async () => {
          if (this.showAllExtras) {
            await this.loadFlightsExtras(this.loyaltyCodes[0], this.isTFXLLC).catch(() =>
              this.onNotValid.emit(true)
            );
          }
        })
        .then(() => this.processed.emit(true))
        .catch(() => this.onNotValid.emit(true));
    } else if (this.showAllExtras) {
      this.loadFlightsExtras(this.loyaltyCodes[0], this.isTFXLLC)
        .catch(() => this.onNotValid.emit(true))
        .then(() => this.processed.emit(true));
    }
    if (this.travellerId && !this.isGuest) {
      this.subscribe(this.userService.getUser(this.travellerId), (user: User) => {
        if (user) {
          this.user = user;
          this.travellerDob = user.dob;
        }
      });
      this.subscribe(this.userService.getUserMi(this.travellerId), (user: User) => {
        if (user && user.preferences) {
          this.seatingPreference = user.preferences.filter(
            (preference: UserPreference) =>
              preference.preferenceKey === PreferenceKey.SeatingPreference
          )?.[0]?.preferenceValue;
        }
      });
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.parentTabId?.currentValue === this.parentTabId) {
      this.disableSelectedSeats();
    }
  }

  getSeat(seat: { selectedSeat: FlightSeatMapSeats; id: string }) {
    this.seat.emit(seat);
  }

  disableSelectedSeats() {
    this.outboundSeats = JSON.parse(JSON.stringify(this.outboundFlightsSeats));
    this.inboundSeats = JSON.parse(JSON.stringify(this.inboundFlightsSeats));
    this.outboundSeats.forEach((outboundFlight: FlightSeatMap, index) => {
      outboundFlight?.seats.forEach((seat: FlightSeatMapSeats) => {
        if (this.selectedSeatWithId[this.flight.outboundFlights[index].id]) {
          this.selectedSeatWithId[this.flight.outboundFlights[index].id].forEach((item) => {
            if (item) {
              if (!item.available) {
                item.available = true;
              }
              if (item.column === seat.column && item.row === seat.row) {
                seat.available = false;
                seat.selected = true;
              }
            }
          });
        }
      });
    });

    this.inboundSeats.forEach((inboundFlight: FlightSeatMap, index) => {
      inboundFlight?.seats.forEach((seat: FlightSeatMapSeats) => {
        if (this.selectedSeatWithId[this.flight.inboundFlights[index]?.id]) {
          this.selectedSeatWithId[this.flight.inboundFlights[index].id].forEach((item) => {
            if (!item.available) {
              item.available = true;
            }
            if (item.column === seat.column && item.row === seat.row) {
              seat.available = false;
              seat.selected = true;
            }
          });
        }
      });
    });
  }

  getLoyalty(loyalty: string, id: string, loyaltyProgram: string) {
    this.loyaltyCode.emit({ loyalty, id, loyaltyProgram });
  }

  /**
   * Load flights seats
   * TODO we definitely need to refactor this function to reduce the complexity
   * @param isTFXLLC
   */
  async loadFlightsSeats(isTFXLLC = false) {
    let errorMessage: { code: string; message: string }[] = [];

    if (isTFXLLC) {
      delete this.flight.__typename;

      const legs = [
        ...this.flight.outboundFlights,
        ...(this.flight.inboundFlights || []).map((x) => ({
          ...x,
          additional: { ...x.additional, isIn: true }
        }))
      ];

      const { results: seats, errors } = await lastValueFrom(
        this.searchService.getFlightSeatMap(
          legs.map((leg) => convertFlightJourneyToInputFlightJourney(leg)),
          this.supplier,
          '',
          this.travellerId,
          this.isGuest
        )
      );

      if (errors.length) {
        errorMessage = errors.map((error) => ({
          code: error.errorCode,
          message: error.errorMessage
        }));
      }

      legs.forEach((leg, index) => {
        const seatMap = seats.find(
          (s) =>
            s?.aircraft?.carrier === leg.operatingCarrier &&
            s?.aircraft?.number == leg.operatingFlightNumber
        );

        this.setLoyaltyCode(seatMap?.loyaltyCode || '', index, false);

        if (!leg.additional?.isIn) {
          this.getLoyalty(
            seatMap?.loyaltyCode,
            this.flight.outboundFlights[index]?.id,
            seatMap?.loyaltyProgram
          );
          this.outboundFlightsSeats.push(seatMap);
        } else {
          this.getLoyalty(
            seatMap?.loyaltyCode,
            this.flight.inboundFlights[index - this.flight.outboundFlights.length + 1]?.id,
            seatMap?.loyaltyProgram
          );
          this.inboundFlightsSeats.push(seatMap);
        }
      });
    } else {
      for (const [index, f] of this.flight.outboundFlights.entries()) {
        delete this.flight.__typename;
        const { results: seats, errors } = await lastValueFrom(
          this.searchService.getFlightSeatMap(
            [convertFlightJourneyToInputFlightJourney(f)],
            this.supplier,
            '',
            this.travellerId,
            this.isGuest
          )
        );

        if (errors.length) {
          errorMessage = errors.map((error) => ({
            code: error.errorCode,
            message: error.errorMessage
          }));
        }

        this.setLoyaltyCode(seats[0].loyaltyCode, index, false);
        this.getLoyalty(seats[0].loyaltyCode, f.id, seats[0].loyaltyProgram);
        this.outboundFlightsSeats.push(seats[0]);
      }

      if (this.flight.inboundFlights?.length) {
        for (const [index, f] of this.flight.inboundFlights.entries()) {
          delete this.flight.__typename;
          const { results: seats, errors } = await lastValueFrom(
            this.searchService.getFlightSeatMap(
              [convertFlightJourneyToInputFlightJourney(f)],
              this.supplier,
              '',
              this.travellerId,
              this.isGuest
            )
          );

          if (errors.length) {
            errorMessage = errors.map((error) => ({
              code: error.errorCode,
              message: error.errorMessage
            }));
          }

          this.setLoyaltyCode(seats[0].loyaltyCode, index, true);
          this.getLoyalty(seats[0].loyaltyCode, f.id, seats[0].loyaltyProgram);
          this.inboundFlightsSeats.push(seats[0]);
        }
      }
    }
    this.inboundSeats = JSON.parse(JSON.stringify(this.inboundFlightsSeats));
    this.outboundSeats = JSON.parse(JSON.stringify(this.outboundFlightsSeats));
    this.disableSelectedSeats();

    await this.checkError(errorMessage);
  }

  async checkError(errorMessage: { code: string; message: string }[]) {
    const noDOBError = errorMessage.find((x) => x.code === 'NO_DOB_WITH_FFN');
    if (noDOBError) {
      await this.modalService.open(
        LightningModalTypes.ModalErrorComponent,
        {},
        { error: noDOBError.message }
      );
    } else if (errorMessage.some((x) => x.message)) {
      await Promise.all(
        errorMessage.map(
          async (error) =>
            await this.modalService.open(
              LightningModalTypes.ModalErrorComponent,
              {},
              { error: error.message }
            )
        )
      );
    }
  }

  async loadFlightsExtras(loyaltyCode = '', isLLC = false) {
    if (isLLC) {
      const legs = [
        ...this.flight.outboundFlights,
        ...(this.flight.inboundFlights || []).map((x) => ({
          ...x,
          additional: { ...x.additional, isIn: true }
        }))
      ];

      const extras = await lastValueFrom(
        this.searchService.searches[ServiceType.Flight].getFlightExtras(
          legs.map((leg) => convertFlightJourneyToInputFlightJourney(leg)),
          this.supplier,
          loyaltyCode,
          this.travellerId
        )
      );

      legs.forEach((leg, index) => {
        if (!leg.additional?.isIn) {
          this.outboundFlightsExtras.push(extras[index]);
        } else {
          this.inboundFlightsExtras.push(extras[index]);
        }
      });

      if (this.supplier === ServiceProvider.Travelfusion) {
        this.luggagePerBooking = extras?.[0]?.luggagePerBooking;
        this.ancillaryPerBooking = extras?.[0]?.ancillaryPerBooking;
        this.luggagePerPax = extras?.[0]?.luggagePerPax;
      }
    } else {
      const [extras] = await lastValueFrom(
        this.searchService.searches[ServiceType.Flight].getFlightExtras(
          this.flight.outboundFlights.map((leg) => convertFlightJourneyToInputFlightJourney(leg)),
          this.supplier,
          loyaltyCode,
          this.travellerId
        )
      );
      this.outboundFlightsExtras.push(extras);

      if (this.supplier === ServiceProvider.Travelfusion) {
        this.luggagePerBooking = extras?.luggagePerBooking;
        this.ancillaryPerBooking = extras?.ancillaryPerBooking;
        this.luggagePerPax = extras?.luggagePerPax;
      }

      if (this.flight.inboundFlights.length) {
        const [extras] = await await lastValueFrom(
          this.searchService.searches[ServiceType.Flight].getFlightExtras(
            this.flight.inboundFlights.map((leg) => convertFlightJourneyToInputFlightJourney(leg)),
            this.supplier,
            loyaltyCode,
            this.travellerId
          )
        );
        this.inboundFlightsExtras.push(extras);
      }
    }
  }

  /**
   * Recalculate flight seats based on loyalty code
   * TODO we definitely need to refactor this function to reduce the complexity
   * @param index
   * @param inbound
   */
  async recalculateFlightSeat(index: number, userId: string, inbound = false) {
    // we want to change this so that applying a loyalty code applies to all that specific user's flights
    const userLoyaltyCode = this.userLoyaltyCode[userId];
    if (this.showAllExtras) {
      // Reload flight extras
      this.outboundFlightsExtras = [];
      this.inboundFlightsExtras = [];
    }
    // all outbounds and inbounds
    let errorMessage: { code: string; message: string }[] = [];
    if (this.isTFXLLC) {
      delete this.flight.__typename;

      const legs = [
        ...this.flight.outboundFlights,
        ...(this.flight.inboundFlights || []).map((x) => ({
          ...x,
          additional: { ...x.additional, isIn: true }
        }))
      ];
      legs.forEach((_, legIdx) => {
        this.activeRequests[legIdx] = true;
      });

      this.loyaltyCodesChange.emit(!!userLoyaltyCode);
      // send every flight
      this.subscribe(
        this.searchService.getFlightSeatMap(
          legs.map((leg) => convertFlightJourneyToInputFlightJourney(leg)),
          this.supplier,
          userLoyaltyCode,
          this.travellerId,
          this.isGuest,
          this.travellerDobConvertedToString
        ),
        (response) => {
          const { results: seats, errors } = response;

          if (errors.length) {
            errorMessage = errors.map((error) => ({
              code: error.errorCode,
              message: error.errorMessage
            }));
            if (errors[0].errorCode === 'NO_DOB_WITH_FFN') {
              // here open a modal telling user to input dob and try again
              this.dobMandatory = true;
              this.modalService.open(
                LightningModalTypes.ModalCustomWarningComponent,
                { centered: true },
                {
                  warningMessage: `Please enter DOB to continue with loyalty for ${this.userName} and select recalculate.`
                }
              );
            }
          }
          if (this.showAllExtras) {
            // Reload flight extras
            this.outboundFlightsExtras = [];
            this.inboundFlightsExtras = [];
            this.loadFlightsExtras(userLoyaltyCode, this.isTFXLLC);
          }

          legs.forEach((leg, legIdx) => {
            const seatMap = seats.find(
              (s) =>
                s?.aircraft?.carrier === leg.operatingCarrier &&
                s?.aircraft?.number == leg.operatingFlightNumber
            );

            this.setLoyaltyCode(seatMap?.loyaltyCode || '', legIdx, false);

            if (!leg.additional?.isIn) {
              this.getLoyalty(
                seatMap?.loyaltyCode,
                this.flight.outboundFlights[legIdx]?.id,
                seatMap?.loyaltyProgram
              );
              this.outboundFlightsSeats[legIdx] = seats[legIdx];
              this.outboundSeats = JSON.parse(JSON.stringify(this.outboundFlightsSeats));
              // do a check to see if we need to replace a selected seat
              let matchingLegHash: string;
              if (Object.keys(this.selectedSeatWithId).includes(leg.id)) {
                matchingLegHash = leg.id;
              }
              if (matchingLegHash) {
                this.overwriteSeat(matchingLegHash, legIdx);
              }
              this.activeRequests[legIdx] = false;
            } else {
              this.getLoyalty(
                seatMap?.loyaltyCode,
                this.flight.inboundFlights[legIdx - this.flight.outboundFlights.length]?.id,
                seatMap?.loyaltyProgram
              );
              this.inboundFlightsSeats[legIdx - this.flight.outboundFlights.length] = seats[legIdx];
              this.inboundSeats = JSON.parse(JSON.stringify(this.inboundFlightsSeats));
              // do a check to see if we need to replace a selected seat
              this.disableSelectedSeats();

              let matchingLegHash: string;
              if (Object.keys(this.selectedSeatWithId).includes(leg.id)) {
                matchingLegHash = leg.id;
              }
              if (matchingLegHash) {
                this.overwriteSeat(matchingLegHash, legIdx, true);
              }
              this.activeRequests[legIdx] = false;
            }
          });
        },
        (error) => {
          // Handle error
          console.error('Error fetching flight seat map:', error);
        }
      );
    } else {
      if (inbound) {
        this.activeRequests[this.flight.outboundFlights.length + index] = true;
        this.subscribe(
          this.searchService.getFlightSeatMap(
            [convertFlightJourneyToInputFlightJourney(this.flight.inboundFlights[index])],
            this.supplier,
            this.loyaltyCodes[this.flight.outboundFlights.length + index],
            this.travellerId,
            this.isGuest,
            this.travellerDobConvertedToString
          ),
          (response) => {
            const { results: seats, errors } = response;

            if (errors.length) {
              errorMessage = errors.map((error) => ({
                code: error.errorCode,
                message: error.errorMessage
              }));
            }

            if (seats[0].loyaltyCode) {
              this.setLoyaltyCode(seats[0].loyaltyCode, index, true);
              this.getLoyalty(
                seats[0].loyaltyCode,
                this.flight.inboundFlights[index].id,
                seats[0].loyaltyProgram
              );
            }

            this.inboundFlightsSeats[index] = seats[0];
            this.activeRequests[this.flight.outboundFlights.length + index] = false;
            if (this.showAllExtras) {
              // Reload flight extras
              this.outboundFlightsExtras = [];
              this.inboundFlightsExtras = [];
              this.loadFlightsExtras(userLoyaltyCode, this.isTFXLLC);
            }
            this.inboundSeats = JSON.parse(JSON.stringify(this.inboundFlightsSeats));
            this.outboundSeats = JSON.parse(JSON.stringify(this.outboundFlightsSeats));
            this.disableSelectedSeats();

            this.checkError(errorMessage);
          },
          (error) => {
            // Handle error
            console.error('Error fetching flight seat map:', error);
          }
        );
      } else {
        this.activeRequests[index] = true;
        this.subscribe(
          this.searchService.getFlightSeatMap(
            [convertFlightJourneyToInputFlightJourney(this.flight.outboundFlights[index])],
            this.supplier,
            userLoyaltyCode,
            this.travellerId,
            this.isGuest,
            this.travellerDobConvertedToString
          ),
          (response) => {
            const { results: seats, errors } = response;

            if (errors.length) {
              errorMessage = errors.map((error) => ({
                code: error.errorCode,
                message: error.errorMessage
              }));
            }

            if (seats[0].loyaltyCode) {
              this.setLoyaltyCode(seats[0].loyaltyCode, index);
              this.getLoyalty(
                seats[0].loyaltyCode,
                this.flight.outboundFlights[index].id,
                seats[0].loyaltyProgram
              );
            }

            this.outboundFlightsSeats[index] = seats[0];
            this.activeRequests[index] = false;
            if (this.showAllExtras) {
              // Reload flight extras
              this.outboundFlightsExtras = [];
              this.inboundFlightsExtras = [];
              this.loadFlightsExtras(userLoyaltyCode, this.isTFXLLC);
            }
            this.inboundSeats = JSON.parse(JSON.stringify(this.inboundFlightsSeats));
            this.outboundSeats = JSON.parse(JSON.stringify(this.outboundFlightsSeats));
            this.disableSelectedSeats();

            this.checkError(errorMessage);
          },
          (error) => {
            // Handle error
            console.error('Error fetching flight seat map:', error);
          }
        );
      }
    }
  }
  overwriteSeat(matchingLegHash: string, legIdx: number, inbound = false): void {
    this.selectedSeatWithId[matchingLegHash] = this.selectedSeatWithId[matchingLegHash].map(
      (selectedSeat) => {
        const { row, column } = selectedSeat;
        const seats = inbound
          ? this.inboundFlightsSeats[legIdx - this.flight.outboundFlights.length]?.seats
          : this.outboundFlightsSeats[legIdx]?.seats;
        const matchingSeat = seats?.find((seat) => seat.row === row && seat.column === column);
        if (matchingSeat && (<FlightSeat>selectedSeat).userId === this.travellerId) {
          selectedSeat.price = matchingSeat.price;
        }
        return selectedSeat;
      }
    );
  }

  getExtras(extras: {
    data: {
      advancedAncillaryOptions: FlightExtrasOptions;
      baggageAllowance: FlightExtrasOptions;
      reducedMobillityAssistance: FlightExtrasOptions;
      flightExtras: FlightExtrasOptions;
    };
    flightIds: [string]; // List of flightIds on the same journey
    updatingFlightId: string;
    travellerId: string;
    key: string; // Updating object key
    luggagePerBooking: boolean;
    ancillaryPerBooking: boolean;
    luggagePerPax: boolean;
  }) {
    this.flightExtras.emit({
      luggagePerBooking: this.luggagePerBooking,
      ancillaryPerBooking: this.ancillaryPerBooking,
      luggagePerPax: this.luggagePerPax,
      ...extras
    });
    this.selectedFlightExtrasChange.emit({
      luggagePerBooking: this.luggagePerBooking,
      ancillaryPerBooking: this.ancillaryPerBooking,
      luggagePerPax: this.luggagePerPax,
      ...extras
    });
    this.extrasPlaceholder = extras.data.flightExtras?.id ? extras.data.flightExtras : null;
    this.baggagePlaceholder = extras.data.baggageAllowance?.id
      ? extras.data.baggageAllowance
      : null;
    this.ancillaryPlaceholder = extras.data.advancedAncillaryOptions?.id
      ? extras.data.advancedAncillaryOptions
      : null;
  }

  setLoyaltyCode(lc: string, index: number, inbound = false, travellerId?: string) {
    if (travellerId) {
      this.userLoyaltyCode[travellerId] = lc;
    }

    if (inbound) {
      this.loyaltyCodes[this.flight.outboundFlights.length + index] = lc;
    } else {
      this.loyaltyCodes[index] = lc;
    }
  }

  setSeatsReceived(received: boolean) {
    this.seatsReceived = received;
  }

  onNavChange(event: NgbNavChangeEvent): void {
    this.disableSelectedSeats();
    const nextId = event.nextId;
    this.activeTab = nextId;
    if (!this.firstTab) {
      this.firstTab = parseInt(event.activeId.replace('ngb-nav-', ''), 10);
    }
    const index = Math.max(parseInt(nextId.replace('ngb-nav-', ''), 10) - this.firstTab, 0);

    let activeFlightID: string;

    if (index < this.flight.outboundFlights.length) {
      activeFlightID = this.flight.outboundFlights[index]?.id;
    } else {
      activeFlightID = this.flight.inboundFlights[index - this.flight.outboundFlights.length]?.id;
    }
    this.activeFlightID.emit({ flightId: activeFlightID, travellerId: this.travellerId });
  }

  // updateDob(timeStamp:moment.Moment, userId) {
  //   // this will be a new resolver, coming soon
  //   this.searchService.updateSelecedtravallerDob(timeStamp, userId);
  // }

  setTravellerDob(date: NgbDateStruct) {
    if (!date) {
      this.travellerDob = null;
      this.travellerDobConvertedToString = '';
      return '';
    }
    const { year, month, day } = date;

    this.travellerDobConvertedToString = `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;

    this.travellerDob = new Date(year, month - 1, day).getTime();
  }
}
